//libs
import React, { memo, useEffect, useState, useCallback, useRef } from 'react';

//styles
import { FooterContainer, Container, LogoutButton, NykaaFashionIcon } from './styles';

//components
import DownloadApp from 'shared/components/MyAccount/components/DownloadApp';
import CustomerInfo from 'shared/components/MyAccount/components/CustomerInfo';
import MyAccountSections from 'shared/components/MyAccount/components/MyAccountSections';
import Footer from 'shared/components/MyAccount/components/Footer';
import Logo from 'shared/components/Logo';
import GamificationNudge from 'shared/components/GamificationNudge';
import GamificationNudgeV2 from 'shared/components/GamificationNudgeV2';

//types
import { IMyAccountProps, IMyAccountSections } from './types';
import { GAMIFICATION_NUDGE, GAMIFICATION_NUDGE_VARIANT_B } from 'shared/components/GamificationNudge/constants';

//constants
import { APP_CONTAINER_CSS } from 'shared/styles/base';
import { LOGIN_EVENTS } from 'shared/helpers/analytics/constants/loginEvents';
import { TEXTS } from 'shared/components/MyAccount/constants';
import { LOGO_TYPE } from 'shared/constants';

//api
import { logoutUser } from 'shared/api/auth';

// analytics
import { onPageLoad } from './analytics';

// helpers
import { loadWidgets } from './helpers';
import NFA from 'shared/helpers/analytics/NFAnalytics';
import storage from 'shared/helpers/storage';
import { clearFreeGiftsSessionKey } from 'shared/components/CartCTA/helpers';
import { getEligibleVariantName } from 'shared/components/ExperimentV2/helpers';

//icons
import Placeholder from './Placeholder';

// types
import { TRACK_TYPE } from 'shared/helpers/analytics/constants';

// context
import { useAppLayoutContext } from 'shared/layouts/AppLayout/AppLayoutContext';
import { TOASTER_TYPES, useToastContext } from 'shared/components/Toast';

// hooks
import { useAuthModalWrapperContext } from 'shared/components/AuthModal/AuthModalWrapperContext';
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';
import { useUserData } from 'shared/components/UserData/hooks';
import { useDomainConfig } from 'shared/domainConfig/context/context';

function MyAccount({
  isUserFetching,
  isLoggedIn,
  setHeaderOptions,
  resetHeaderOptions,
}: IMyAccountProps) {
  const { setCSS } = useAppLayoutContext();
  const { isEligible: isGamificationNudgeEnabled = false, variant: gamificationNudgeVariant = '' } =
    getEligibleVariantName(GAMIFICATION_NUDGE);
  const { clearUserData } = useUserData();
  const [sectionsData, setSectionsData] = useState<IMyAccountSections[]>([]);
  const [footerData, setFooterData] = useState<IMyAccountSections[]>([]);
  const [appData, setAppData] = useState<IMyAccountSections[]>([]);
  const [logoutData, setLogoutData] = useState({});
  const { WIDGETS, CONTENT } = useRemoteConfig('myAccount');
  const { isAuthModalVisible } = useAuthModalWrapperContext();
  const pageScrollPosition = useRef<null | number>();
  const { showToast } = useToastContext();

  const { MY_ACCOUNT_APP_DOWNLOAD } = useDomainConfig();
  const { MY_ACCOUNT_DATA } = useDomainConfig();

  useEffect(onPageLoad, []);

  useEffect(() => {
    try {
      let content = JSON.parse(JSON.stringify(MY_ACCOUNT_DATA.CONTENT));
      let logoutInfo;
      if (WIDGETS['LOGOUT'])
        logoutInfo = { ...MY_ACCOUNT_DATA.WIDGETS['LOGOUT'], ...WIDGETS['LOGOUT'] };
      else logoutInfo = MY_ACCOUNT_DATA.WIDGETS['LOGOUT'];
      setLogoutData(logoutInfo);
      content = content?.map((section) => {
        if (CONTENT[section.key]) {
          return loadWidgets(MY_ACCOUNT_DATA.WIDGETS, WIDGETS, {
            ...section,
            ...CONTENT[section.key],
          });
        }
        return loadWidgets(MY_ACCOUNT_DATA.WIDGETS, WIDGETS, section);
      });
      const userData = [],
        apps = [],
        footer = [];
      content.forEach((obj) => {
        if (!obj.isEnabled) return;
        switch (obj.type) {
          case 'sections':
            userData.push(obj);
            break;
          case 'footer':
            footer.push(obj);
            break;
          case 'apps':
            apps.push(obj);
            break;
          default:
            break;
        }
      });
      setSectionsData(userData);
      setAppData(apps);
      setFooterData(footer);
    } catch (error) {
      //do nothing
    }
  }, [CONTENT, WIDGETS, MY_ACCOUNT_DATA]);

  useEffect(() => {
    setCSS(APP_CONTAINER_CSS);

    return () => {
      resetHeaderOptions();
    };
  }, [resetHeaderOptions, setCSS, setHeaderOptions]);

  const onLogoutClick = useCallback(() => {
    //logout clicked
    NFA.track({
      type: TRACK_TYPE.EVENT,
      event: LOGIN_EVENTS.LOGOUT_START,
    });

    NFA.reset();

    logoutUser()
      .then((res) => {
        if (res.status === 'success') {
          // clear all user specific data from device on logout
          clearUserData();
          storage.clear();
          clearFreeGiftsSessionKey();
          showToast({
            type: TOASTER_TYPES.SUCCESS,
            message: res?.response?.message || TEXTS.USER_LOGGEDOUT,
          });
          window.location.reload();
        }
      })
      .catch((e) =>
        showToast({ type: TOASTER_TYPES.ERROR, message: e?.message || TEXTS.API_ERROR_MESSAGE })
      );
  }, [clearUserData, showToast]);

  useEffect(() => {
    if (isUserFetching) return;

    if (isAuthModalVisible) {
      pageScrollPosition.current = window.scrollY;
      return;
    }
    if (pageScrollPosition.current !== null) {
      window.scrollTo(0, pageScrollPosition.current);
      pageScrollPosition.current = null;
      return;
    }
  }, [isAuthModalVisible, isUserFetching]);

  if (isUserFetching) {
    return <Placeholder />;
  }
  return (
    <>
      <Container>
        <CustomerInfo />
        {isGamificationNudgeEnabled &&
          (gamificationNudgeVariant === GAMIFICATION_NUDGE_VARIANT_B ? <GamificationNudgeV2 /> : <GamificationNudge />)}
        {!!sectionsData.length &&
          sectionsData.map((section_data) => {
            return section_data.children?.map((section, index) => (
              <MyAccountSections key={index} section_data={section} isLoggedIn={isLoggedIn} />
            ));
          })}
        {!!appData.length &&
          MY_ACCOUNT_APP_DOWNLOAD &&
          appData.map((app) => (
            <DownloadApp key={app.key} text={app.title} sections={app.children} />
          ))}
      </Container>
      {(!!footerData?.length || isLoggedIn) && (
        <FooterContainer>
          {footerData.map((footer) => (
            <>
              <NykaaFashionIcon>
                <Logo type={LOGO_TYPE.FOOTER} />
              </NykaaFashionIcon>
              <Footer footerData={footer.children} isLoggedIn={isLoggedIn} />
            </>
          ))}
          {!!isLoggedIn && !!logoutData.title?.length && (
            <LogoutButton onClick={onLogoutClick}>{logoutData.title}</LogoutButton>
          )}
        </FooterContainer>
      )}
    </>
  );
}

export default memo(MyAccount);
